// ---- Declarative part-list models ----
// The interpreter that turns a JSON model document (content/models/*.json) into
// meshkit prim calls. A part mirrors one prim signature 1:1 — the chunky prims
// (box/blob/cone/cylinder/tube) plus the plan-28 rounded prims (ball/arc/lathe;
// bone is from→to like rod) — plus an optional Euler `rot` (radians, XYZ;
// tube keeps its axis flip only, bone derives orientation) and three declarative
// modifiers that replace the common authoring idioms:
//   mirror: true               the `for (s of [-1,1])` left/right pair — emits the
//                              part twice, the copy with its FINAL x negated
//   repeat: {count, step}      a run of copies offset by step each iteration
//   repeat: {count, ring:{r}}  copies on a circle in the xz plane (crown points)
//   anchor: "muzzle"           `at` becomes an offset from a named point published
//                              by the model's archetype (quadruped/humanoid)
// Colors are palette keys or raw "#rrggbb"; a palette entry may derive from
// another via { base, shade } (the shade() idiom).
//
// Pure + THREE-free (Prims is a type import): imported by the game, the headless
// server (via load.ts), validation, and tests.

import type { Prims } from "../meshkit.ts";
import type { WearSlot } from "../shared.ts";
import { shade } from "../color.ts";

export type Vec3 = [number, number, number];

/** Named color table. Values: "#rrggbb", or { base, shade } deriving from an
 *  earlier palette key (or a raw hex) via shade(). */
export type PaletteDef = Record<string, string | { base: string; shade: number }>;

export type PartRepeat = {
  count: number;
  /** Per-iteration offset (a run: toes, planks). */
  step?: Vec3;
  /** Place copies on a circle of this radius in the xz plane (crown points).
   *  Iteration i sits at angle i/count · 2π (angle 0 = +x). */
  ring?: { r: number };
};

type PartBase = {
  /** Palette key or "#rrggbb". */
  color: string;
  /** Position — absolute (feet-space) or an offset from `anchor` when set. */
  at: Vec3;
  /** Euler XYZ radians applied after scale (box long-axis was +Y). Use for
   *  diagonal limbs — e.g. a bow segment from (x0,y0)→(x1,y1) sits at the
   *  midpoint with rot.z = −atan2(dx, dy) and size[1] = segment length. */
  rot?: Vec3;
  /** Offset from this archetype anchor point instead of the model origin. */
  anchor?: string;
  /** Emit INSIDE this named limb group (meshkit inLimb) so the part rides the
   *  joint — ears/mane on a quadruped's `neck`, tail hair on `tail`. Positions
   *  stay absolute feet-space. Falls back to root emission when the build has
   *  no such limb (an unjointed base sharing the doc). */
  limb?: string;
  /** Also emit a copy with the final x negated (left/right symmetry). */
  mirror?: boolean;
  repeat?: PartRepeat;
};

/** Rod parts use `from`/`to` instead of `at` (orientation is derived). */
export type RodPart = {
  prim: "rod";
  from: Vec3;
  to: Vec3;
  r: number;
  color: string;
  anchor?: string;
  limb?: string;
  mirror?: boolean;
  repeat?: PartRepeat;
};

/** Tapered capsule between two points — the rounded limb/mass prim (plan 28).
 *  Like rod it uses `from`/`to` (mirror negates BOTH ends' x). Caps sit
 *  centered ON the endpoints, so the mesh extends r0/r1 PAST them — bury caps
 *  in adjoining masses. `depth` scales the section along local z (for a bone
 *  laid along world z that's the world VERTICAL); `bulge` = muscle belly. */
export type BonePart = {
  prim: "bone";
  from: Vec3;
  to: Vec3;
  r0: number;
  r1: number;
  depth?: number;
  bulge?: { amount: number; at?: number };
  color: string;
  anchor?: string;
  limb?: string;
  mirror?: boolean;
  repeat?: PartRepeat;
};

export type Part =
  | (PartBase &
      (
        | { prim: "box"; size: Vec3 }
        | { prim: "blob"; r: number }
        | { prim: "cone"; r: number; h: number } // at[1] = baseY
        | { prim: "cylinder"; rTop: number; rBottom: number; h: number } // at[1] = baseY
        | { prim: "tube"; r: number; len: number; axis?: "x" | "z" }
        // Rounded prims (plan 28) — smooth toon ramp, organic masses.
        | { prim: "ball"; r: number; depth?: number }
        | { prim: "arc"; r: number; tube: number; sweep: number }
        // at = [x, baseY, z]; profile = [radius, y] pairs bottom→top (y relative
        // to baseY); frontBulge = [y, dz] pairs pushing only the +z face.
        | { prim: "lathe"; profile: [number, number][]; depth?: number; segs?: number; frontBulge?: [number, number][] }
      ))
  | RodPart
  | BonePart;

/** Per-instance seeded variation knobs (see content schema `variationSchema`).
 *  Visual only — colliders/HUD heights stay on the NPC spec. */
export type VariationDef = {
  shade?: number;
  scale?: number;
  gaitPhase?: boolean;
  gaitSpeed?: number;
};

/** Local-space named points on a held model. `grip` is the seat (item hold.hilt
 *  must match); string/tip/head/guard are secondary landmarks for inspect +
 *  validate + dual-hand pose scoring (guard = crossguard / hand-stop on hilts). */
export type ModelLandmarks = {
  grip?: Vec3;
  string?: Vec3;
  tip?: Vec3;
  head?: Vec3;
  /** Crossguard / hand-stop along the shaft (greatswords). Used to score off-hand
   *  placement: tAlong past guard ⇒ fist on the bar, not the leather. */
  guard?: Vec3;
};

/** A content/models/*.json document. `body` is archetype-specific (quadruped /
 *  humanoid options with palette-key colors); `parts` is the whole model for the
 *  "parts" archetype, or the details layer on top of an archetype body.
 *  A doc with `base` is a VARIANT: it merges over the named base doc
 *  (src/model/variants.ts) — palette shallow, body deep, parts appended,
 *  slots shallow, landmarks shallow. `slots` fill ARCHETYPE_SLOTS;
 *  `variation` = seeded jitter; `landmarks` = grip seat for held props. */
export type ModelDoc = {
  $schema?: string;
  id: string;
  archetype: "parts" | "quadruped" | "humanoid" | "bird" | "bug";
  base?: string;
  palette?: PaletteDef;
  body?: Record<string, unknown>;
  parts?: Part[];
  slots?: Record<string, string>;
  variation?: VariationDef;
  /** Grip/seat + secondary points for held props (weapons, shields, rods). */
  landmarks?: ModelLandmarks;
};

/** Anchor names each archetype publishes — the single source validation and the
 *  archetype builders both conform to (asserted in parts.test.ts). */
export const ARCHETYPE_ANCHORS: Record<ModelDoc["archetype"], readonly string[]> = {
  parts: ["root"],
  quadruped: ["root", "head", "muzzle", "crown", "back", "front", "rear", "rump", "flank"],
  humanoid: [
    "root", "crown", "face", "chest", "back", "beltFront", "handL", "handR",
    "torso", "legL", "legR", "footL", "footR",
    // Wear helpers (torsowear fillers may set part.anchor to these — see
    // WEAR_FILLER_ANCHORS / wearable-authoring). Shelf = shoulder joint rest.
    "shoulderShelfL", "shoulderShelfR", "collar", "belt",
  ],
  bird: ["root", "head", "beak", "crown", "back", "breast", "tail", "wingL", "wingR"],
  bug: ["root", "head", "face", "crown", "back", "abdomen", "rear", "flank"],
};

/**
 * Anchors a content/parts filler may name on a prim (`part.anchor`) when the
 * host passes the full archetype anchor map (slot fill: `root` = slot seat).
 * Keeps agents off freehand mid-chest numbers for collar / shoulder / belt.
 *
 * SIZING contract: wear-slot fillers (torsowear, legwearL/R, footwearL/R,
 * back) are authored against the AVERAGE-MALE body baseline (src/wear/seat.ts
 * AVERAGE_MALE_BODY) — buildFromDoc radially scales them per body via
 * src/model/wear-scale.ts (WearScaleCtx), so placement comes from anchors and
 * SIZE comes from baseline + clearance, never a per-body number. Decorative
 * doc.parts never scale.
 */
export const WEAR_FILLER_ANCHORS: readonly string[] = [
  "root",
  "torso", "chest", "back", "beltFront", "belt",
  "collar", "shoulderShelfL", "shoulderShelfR",
  "legL", "legR", "footL", "footR",
  "crown", "face",
];

/** A named SLOT a model doc can fill (ModelDoc.slots):
 *  - anchor slots take a parts-library doc (content/parts) emitted at the
 *    anchor — INSIDE the named limb when the rig publishes it (helmets nod
 *    with the head; falls back to root emission on unjointed builds);
 *  - socket slots (held/offhand) take an ITEM TYPE, seated into the hand
 *    socket at the dressing layer (src/npc/dress.ts) via the same weapon-
 *    seating path the player uses. */
export type SlotDef = { anchor: string; limb?: string } | { socket: "handMain" | "handOff" };

export const ARCHETYPE_SLOTS: Record<ModelDoc["archetype"], Record<string, SlotDef>> = {
  parts: {},
  quadruped: {
    headwear: { anchor: "crown" },
    saddle: { anchor: "back" },
  },
  humanoid: {
    headwear: { anchor: "crown", limb: "head" },
    face: { anchor: "face", limb: "head" },
    back: { anchor: "back", limb: "torso" },
    waist: { anchor: "beltFront" },
    held: { socket: "handMain" },
    offhand: { socket: "handOff" },
    // Wearable body slots (the paperdoll's torso/legs/feet — see WearSpec).
    // legs/feet come as an L/R pair: the SAME parts doc is emitted at both
    // anchors, so author those pieces symmetric around their anchor (x ≈ 0).
    // On rigless/skirted builds the limb is absent → root emission (static).
    torsowear: { anchor: "torso", limb: "torso" },
    legwearL: { anchor: "legL", limb: "hipL" },
    legwearR: { anchor: "legR", limb: "hipR" },
    footwearL: { anchor: "footL", limb: "kneeL" },
    footwearR: { anchor: "footR", limb: "kneeR" },
  },
  bird: {
    headwear: { anchor: "crown" },
    back: { anchor: "back" },
  },
  bug: {
    back: { anchor: "back" },
  },
};

/** Which humanoid model slots a wearable's `part` fills, per body area (the
 *  paperdoll's WearSlot vocabulary → the slot names above). Lives HERE, beside
 *  ARCHETYPE_SLOTS, so renaming/restructuring a slot touches one file. Legs and
 *  feet come as an L/R pair — the SAME parts doc lands at both anchors. */
export const WEAR_MODEL_SLOTS: Record<WearSlot, string[]> = {
  head: ["headwear"],
  torso: ["torsowear"],
  legs: ["legwearL", "legwearR"],
  feet: ["footwearL", "footwearR"],
  back: ["back"],
};

// ---------------------------------------------------------------------------
// Palette
// ---------------------------------------------------------------------------

export function parseHex(c: string): number | undefined {
  const m = /^#([0-9a-fA-F]{6})$/.exec(c);
  return m ? parseInt(m[1]!, 16) : undefined;
}

/** Resolve a palette to plain color numbers. Derived entries ({ base, shade })
 *  may reference any other palette key (in any order) or a raw hex. Throws on
 *  unresolvable/circular references — validation reports these pre-load. */
export function resolvePalette(def: PaletteDef): Record<string, number> {
  const out: Record<string, number> = {};
  const resolving = new Set<string>();
  const resolve = (key: string): number => {
    if (out[key] !== undefined) return out[key]!;
    const v = def[key];
    if (v === undefined) throw new Error(`palette: unknown key "${key}"`);
    if (resolving.has(key)) throw new Error(`palette: circular reference at "${key}"`);
    resolving.add(key);
    let n: number;
    if (typeof v === "string") {
      const hex = parseHex(v);
      if (hex === undefined) throw new Error(`palette: "${key}" is not #rrggbb`);
      n = hex;
    } else {
      const base = def[v.base] !== undefined ? resolve(v.base) : parseHex(v.base);
      if (base === undefined) throw new Error(`palette: "${key}" base "${v.base}" unresolvable`);
      n = shade(base, v.shade);
    }
    resolving.delete(key);
    out[key] = n;
    return n;
  };
  for (const key of Object.keys(def)) resolve(key);
  return out;
}

/** A part's color: palette key first, else raw hex. */
export function colorOf(c: string, palette: Record<string, number>): number {
  const fromPalette = palette[c];
  if (fromPalette !== undefined) return fromPalette;
  const hex = parseHex(c);
  if (hex === undefined) throw new Error(`color "${c}" is neither a palette key nor #rrggbb`);
  return hex;
}

// ---------------------------------------------------------------------------
// Interpreter
// ---------------------------------------------------------------------------

function emit(p: Prims, part: Part, x: number, y: number, z: number, color: number): void {
  if (part.prim === "rod" || part.prim === "bone") return; // from/to prims go through emitSpan
  const rot = part.rot;
  switch (part.prim) {
    case "box": p.box(x, y, z, part.size[0], part.size[1], part.size[2], color, rot); break;
    case "blob": p.blob(x, y, z, part.r, color, rot); break;
    case "cone": p.cone(x, y, z, part.r, part.h, color, rot); break;
    case "cylinder": p.cylinder(x, y, z, part.rTop, part.rBottom, part.h, color, rot); break;
    // tube already bakes its axis flip; free rot isn't exposed (use box/cylinder/rod).
    case "tube": p.tube(x, y, z, part.r, part.len, color, part.axis ?? "x"); break;
    case "ball": p.ball(x, y, z, part.r, color, part.depth, rot); break;
    case "arc": p.arc(x, y, z, part.r, part.tube, color, part.sweep, rot); break;
    case "lathe": p.lathe(x, y, z, part.profile, color, part.depth, part.segs, rot, part.frontBulge); break;
  }
}

/** rod/bone — the from→to prims (orientation derived, mirror flips both ends). */
function emitSpan(
  p: Prims,
  part: RodPart | BonePart,
  fx: number, fy: number, fz: number,
  tx: number, ty: number, tz: number,
  color: number,
): void {
  if (part.prim === "rod") p.rod(fx, fy, fz, tx, ty, tz, part.r, color);
  else p.bone(fx, fy, fz, tx, ty, tz, part.r0, part.r1, color, part.depth, part.bulge);
}

/** Lowest y any part in the list reaches (its bottom face/point), assuming root
 *  anchoring at [0,0,0]. Pure arithmetic over the prim shapes — used to LIFT a
 *  slot-relative parts doc (a hat authored around the crown anchor) so it rests
 *  on the ground when it doubles as a wearable's drop model. */
export function partsMinY(parts: Part[]): number {
  let min = 0;
  for (const part of parts) {
    const count = part.repeat?.count ?? 1;
    const stepY = part.repeat?.step?.[1] ?? 0;
    if (part.prim === "rod" || part.prim === "bone") {
      const ends: [number, number][] = part.prim === "rod"
        ? [[part.from[1], part.r], [part.to[1], part.r]]
        : [[part.from[1], part.r0], [part.to[1], part.r1]]; // caps extend r past the endpoint
      for (const [endY, r] of ends) {
        for (const y of [endY, endY + stepY * (count - 1)]) {
          const bottom = y - r;
          if (bottom < min) min = bottom;
        }
      }
      continue;
    }
    // Only the y extremes matter: iteration 0 and the last step (ring is xz-only).
    for (const y of [part.at[1], part.at[1] + stepY * (count - 1)]) {
      let bottom = y;
      if (part.prim === "box") bottom = y - part.size[1] / 2;
      else if (part.prim === "blob" || part.prim === "tube" || part.prim === "ball") bottom = y - part.r;
      else if (part.prim === "arc") bottom = y - part.tube; // anchored at the low point
      else if (part.prim === "lathe") bottom = y + Math.min(...part.profile.map(([, py]) => py));
      // cone/cylinder: at[1] is already the base
      if (bottom < min) min = bottom;
    }
  }
  return min;
}

function partOffsets(part: Part, i: number, count: number): Vec3 {
  if (part.repeat?.step) {
    return [
      part.repeat.step[0] * i,
      part.repeat.step[1] * i,
      part.repeat.step[2] * i,
    ];
  }
  if (part.repeat?.ring) {
    const a = (i / count) * Math.PI * 2;
    return [Math.cos(a) * part.repeat.ring.r, 0, Math.sin(a) * part.repeat.ring.r];
  }
  return [0, 0, 0];
}

/** Interpret a part list against a prim set. `anchors` comes from the model's
 *  archetype (always at least { root: [0,0,0] }). */
export function interpretParts(
  p: Prims,
  parts: Part[],
  palette: Record<string, number>,
  anchors: Record<string, Vec3>,
): void {
  for (const part of parts) {
    const base = anchors[part.anchor ?? "root"];
    if (!base) throw new Error(`part anchor "${part.anchor}" not published by this archetype`);
    const color = colorOf(part.color, palette);
    const count = part.repeat?.count ?? 1;
    const emitPart = () => {
      for (let i = 0; i < count; i++) {
        const [ox, oy, oz] = partOffsets(part, i, count);
        if (part.prim === "rod" || part.prim === "bone") {
          const fx = base[0] + part.from[0] + ox;
          const fy = base[1] + part.from[1] + oy;
          const fz = base[2] + part.from[2] + oz;
          const tx = base[0] + part.to[0] + ox;
          const ty = base[1] + part.to[1] + oy;
          const tz = base[2] + part.to[2] + oz;
          emitSpan(p, part, fx, fy, fz, tx, ty, tz, color);
          if (part.mirror) emitSpan(p, part, -fx, fy, fz, -tx, ty, tz, color);
          continue;
        }
        const x = base[0] + part.at[0] + ox;
        const y = base[1] + part.at[1] + oy;
        const z = base[2] + part.at[2] + oz;
        emit(p, part, x, y, z, color);
        if (part.mirror) emit(p, part, -x, y, z, color);
      }
    };
    // Ride a named joint (ears/mane on `neck`) — root emission when absent.
    if (part.limb) p.inLimb(part.limb, emitPart);
    else emitPart();
  }
}
